import React, { useCallback, useState } from 'react';
import type { MouseEvent } from 'react';

import { twMerge } from 'tailwind-merge';

import { TbMenu2 } from 'react-icons/tb';

import Icon from 'components-new/icon/icon';

import type { User } from 'state/__generated__/tadaahAPI';

import OrganisationSelect from 'pages/organization/components/organisation-select/organisation-select';
import type { OrganisationType } from 'pages/organization/components/organisation-select/organisation-select';

import AvatarMenu from './avatar-menu/avatar-menu';
import NavigationItem from './navigation-item';
import MobileNavMenu from './mobile-nav-menu';
import type { AvatarMenuItemType } from './avatar-menu/avatar-menu-item';
import type { AvatarMenuViewType } from './avatar-menu/avatar-menu-view';

export type NavigationBarLink = {
  id?: string;
  link: string;
  title: string;
  isSelected?: boolean;
  onClick?: (evt: MouseEvent<HTMLAnchorElement>) => void;
};

export interface NavigationBarProps {
  showGradientTopBar?: boolean;
  links: NavigationBarLink[];
  variant?: 'neutral' | 'purple';
  className?: string;
  navigationItemClassName?: string;
  navigationItemTypographyClassName?: string;
  selectedOrganisation?: OrganisationType;
  organisations?: OrganisationType[];
  logo?: string;
  user: User | undefined;
  accountMenuItems: AvatarMenuItemType[];
  accountMenuViews: AvatarMenuViewType[];
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  showGradientTopBar,
  links,
  variant = 'neutral',
  className,
  navigationItemClassName,
  navigationItemTypographyClassName,
  selectedOrganisation,
  organisations,
  logo,
  user,
  accountMenuItems,
  accountMenuViews,
}) => {
  const [open, setOpen] = useState(false);

  const selectedView = accountMenuViews.find((view) => view.selected);

  const navigationBarClassNames = twMerge(
    'relative flex flex-col justify-center items-start py-4',
    'md:flex-row md:justify-between md:py-0 h-[65px]',
    'border-b border-gray100',
    'px-2 lg:px-6',
    variant === 'neutral' ? 'bg-background-navigation' : 'bg-purple',
  );

  const navigationClassNames = twMerge(
    'pt-4 md:pt-0 md:mt-0 px-0 whitespace-nowrap',
    'flex flex-col md:flex-row md:gap-x-4 w-full md:w-auto md:border-none',
    'md:items-center md:h-full',
    'absolute md:static z-[-1] md:z-auto left-0',
    'transition-all duration-300 ease-in',
    open
      ? 'top-[50px] opacity-100 mt-3 pb-3 border-b'
      : 'hidden md:flex opacity-0',
    'md:opacity-100 md:h-full',
    variant === 'neutral'
      ? 'bg-background-navigation md:bg-transparent'
      : 'bg-purple',
  );

  const iconClassname = twMerge(
    'text-4xl absolute right-7 my-auto cursor-pointer md:invisible',
    variant === 'neutral' && 'text-gray-800',
    variant === 'purple' && 'text-gray-100',
  );

  const background =
    'linear-gradient(90deg, #FED630 5.21%, #5DC4BF 40.63%, #5DC4BF 52.61%, #43344D 93.75%';

  const logoPath = 'tadaah-logo-2024.svg';

  const tadaahLogo = `${window.STATIC_URL}assets/${logoPath}`;

  const onMenuOpenClicked = useCallback(() => setOpen((prev) => !prev), []);

  return (
    <div className={twMerge('flex flex-col sticky top-0 z-10', className)}>
      {showGradientTopBar && <div style={{ background }} className="h-2" />}
      <div className={navigationBarClassNames}>
        <a
          className="flex justify-center items-center z-20 p-2 md:h-full rounded hover:bg-background-hover cursor-pointer"
          href="/"
        >
          <img
            src={logo || tadaahLogo}
            height="35px"
            width="128px"
            alt="logo"
          />
        </a>
        <div className="md:absolute md:left-0 md:right-0 top-0 md:mx-auto md:h-full">
          <div className="flex justify-center items-center gap-2 lg:gap-4 h-full">
            <div id="navigation" className={navigationClassNames}>
              {links.map((link) => {
                return (
                  <NavigationItem
                    key={link.id}
                    {...link}
                    className={navigationItemClassName}
                    variant={variant}
                    typographyClassName={navigationItemTypographyClassName}
                  />
                );
              })}
              <MobileNavMenu
                selectedOrganisation={selectedOrganisation}
                organisations={organisations}
                accountMenuItems={accountMenuItems}
                accountMenuViews={accountMenuViews}
                user={user}
              />
            </div>
          </div>
        </div>
        <Icon
          onClick={onMenuOpenClicked}
          icon={TbMenu2}
          size={28}
          className={iconClassname}
        />
        <div className="hidden md:flex h-auto md:h-full md:low-row md:items-center md:justify-center">
          {selectedView?.type === 'organisation' && organisations && (
            <OrganisationSelect organisationOptions={organisations} />
          )}
          <div>
            <AvatarMenu
              user={user}
              menuItems={accountMenuItems}
              views={accountMenuViews}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationBar;
